import { useState, useEffect, useRef, DragEvent } from 'react';
import Icon, { IconType } from 'components/Icon';
import FontInput from 'components/Widget/Input/FontInput';
import { Attachment, DesignSystemFont, GoogleFont } from 'dragonmouse-shared/interfaces';
import forge from 'apis/forge';
import { uploadFile, UploadTracker } from 'utils';
import {
	getAvailableWeights,
	getFallbackOptions,
	getFormatFromFile,
	inferFontNameFromFilename,
	isValidFontFile,
} from 'font';
import * as S from './styles';

interface FontSelectorProps {
	value: DesignSystemFont[];
	onChange: (fonts: DesignSystemFont[]) => void;
	onFontCommit?: (index: number, fonts: DesignSystemFont[]) => void;
}

const FontSelector = ({ value, onChange, onFontCommit }: FontSelectorProps) => {
	const [googleFonts, setGoogleFonts] = useState<GoogleFont[]>([]);
	const [uploadingFiles, setUploadingFiles] = useState<Record<string, UploadTracker>>({});
	const [creatingIndex, setCreatingIndex] = useState<number | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const dragCounterRef = useRef(0);
	const rowDragCounterRef = useRef(0);
	// Always reflects the current value prop so async upload callbacks don't use stale closures
	const valueRef = useRef(value);
	valueRef.current = value;

	useEffect(() => {
		const loadFonts = async () => {
			try {
				const fonts = await forge.getGoogleFonts();
				setGoogleFonts(fonts);
			} catch (err) {
				console.error('Error loading Google Fonts:', err);
			}
		};
		loadFonts();
	}, []);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onFontFamilyChange = (index: number, fontFamily: string) => {
		const newFonts = [...value];
		const availableWeights = getAvailableWeights(fontFamily, googleFonts);
		const currentWeights = newFonts[index].fontWeight || ['400'];
		const validWeights = currentWeights.filter((w) => availableWeights.includes(w));

		const fallbackOpts = getFallbackOptions(fontFamily, googleFonts);
		const appropriateFallback = fallbackOpts[0].value;

		newFonts[index] = {
			...newFonts[index],
			fontFamily,
			fontWeight: validWeights.length > 0 ? validWeights : ['400'],
			fallback: appropriateFallback,
		};
		onChange(newFonts);
	};

	const onFontFamilyCommit = (index: number, fontFamily: string) => {
		if (value[index]?.fontFamily === fontFamily) return;

		const newFonts = [...value];
		const availableWeights = getAvailableWeights(fontFamily, googleFonts);
		const currentWeights = newFonts[index].fontWeight || ['400'];
		const validWeights = currentWeights.filter((w) => availableWeights.includes(w));

		const fallbackOpts = getFallbackOptions(fontFamily, googleFonts);
		const appropriateFallback = fallbackOpts[0].value;

		newFonts[index] = {
			...newFonts[index],
			fontFamily,
			fontWeight: validWeights.length > 0 ? validWeights : ['400'],
			fallback: appropriateFallback,
		};
		setCreatingIndex(null);
		if (onFontCommit) {
			onFontCommit(index, newFonts);
		}
	};

	const onFontCancelCreate = (index: number) => {
		const newFonts = value.filter((_, i) => i !== index);
		setCreatingIndex(null);
		onChange(newFonts);
	};

	const onWeightToggle = (index: number, weight: string) => {
		const newFonts = [...value];
		const currentWeights = newFonts[index].fontWeight || ['400'];

		if (currentWeights.includes(weight)) {
			if (currentWeights.length > 1) {
				newFonts[index] = {
					...newFonts[index],
					fontWeight: currentWeights.filter((w) => w !== weight),
				};
			}
		} else {
			newFonts[index] = {
				...newFonts[index],
				fontWeight: [...currentWeights, weight].sort(),
			};
		}
		onChange(newFonts);
		if (onFontCommit) {
			onFontCommit(index, newFonts);
		}
	};

	const onAddGoogleFont = () => {
		const newIndex = value.length;
		const newFonts: DesignSystemFont[] = [
			...value,
			{
				fontFamily: '',
				source: 'google' as const,
				fontWeight: ['400', '700'],
				fallback: 'sans-serif',
			},
		];
		onChange(newFonts);
		setCreatingIndex(newIndex);
	};

	const onRemoveFont = (index: number) => {
		if (value.length <= 1) return;
		const newFonts = value.filter((_, i) => i !== index);
		onChange(newFonts);
		if (onFontCommit) {
			onFontCommit(index, newFonts);
		}
	};

	const onFileUpload = async (index: number, format: 'woff2' | 'woff', file: File) => {
		if (!isValidFontFile(file)) {
			console.error('Invalid font file format. Please upload WOFF or WOFF2 files.');
			return;
		}

		const uploadKey = `${index}-${format}`;
		const tracker: UploadTracker = {
			filename: file.name,
			contentType: file.type,
			size: file.size,
			uniqueId: uploadKey,
		};

		setUploadingFiles((prev) => ({ ...prev, [uploadKey]: tracker }));

		await uploadFile(file, {
			onLoadingStart: () => {},
			onLoadingEnd: () => {
				setUploadingFiles((prev) => {
					const next = { ...prev };
					delete next[uploadKey];
					return next;
				});
			},
			onSuccess: (attachment: Attachment) => {
				const newFonts = [...valueRef.current];
				const currentFont = newFonts[index];
				const inferredName = inferFontNameFromFilename(file.name);
				const shouldUpdateName =
					currentFont.fontFamily === 'Custom Font' || currentFont.fontFamily === '';

				newFonts[index] = {
					...currentFont,
					fontFamily: shouldUpdateName ? inferredName : currentFont.fontFamily,
					files: {
						...currentFont.files,
						[format]: attachment.storagePath,
					},
				};
				onChange(newFonts);
				if (onFontCommit) {
					onFontCommit(index, newFonts);
				}
			},
			onError: (error) => {
				console.error('Error uploading font file:', error);
			},
		});
	};

	const triggerFileUpload = (index: number) => {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = '.woff,.woff2';
		input.onchange = (e) => {
			const files = (e.target as HTMLInputElement).files;
			if (files && files.length > 0) {
				const file = files[0];
				const format = getFormatFromFile(file);
				if (format) {
					onFileUpload(index, format, file);
				}
			}
		};
		input.click();
	};

	// Handle dropped or browsed custom font files for a new font entry
	const processDroppedFiles = async (files: File[]) => {
		const validFiles = files.filter(isValidFontFile);
		if (validFiles.length === 0) return;

		const newIndex = value.length;
		const newFont: DesignSystemFont = {
			fontFamily: '',
			source: 'custom' as const,
			fallback: 'sans-serif',
			files: {},
		};
		const newFonts = [...value, newFont];
		onChange(newFonts);

		for (const file of validFiles) {
			const format = getFormatFromFile(file);
			if (format) {
				await onFileUpload(newIndex, format, file);
			}
		}
	};

	const onDragEnter = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		dragCounterRef.current += 1;
		setIsDragging(true);
	};

	const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		dragCounterRef.current -= 1;
		if (dragCounterRef.current === 0) {
			setIsDragging(false);
		}
	};

	const onDragOver = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
	};

	const onDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		dragCounterRef.current = 0;
		setIsDragging(false);
		const files = Array.from(e.dataTransfer.files);
		processDroppedFiles(files);
	};

	const onRowDragEnter = (e: DragEvent<HTMLDivElement>, index: number) => {
		e.preventDefault();
		e.stopPropagation();
		if (dragOverIndex !== null && dragOverIndex !== index) {
			rowDragCounterRef.current = 0;
		}
		rowDragCounterRef.current += 1;
		setDragOverIndex(index);
	};

	const onRowDragLeave = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		rowDragCounterRef.current -= 1;
		if (rowDragCounterRef.current === 0) {
			setDragOverIndex(null);
		}
	};

	const onRowDrop = (e: DragEvent<HTMLDivElement>, index: number) => {
		e.preventDefault();
		e.stopPropagation();
		rowDragCounterRef.current = 0;
		setDragOverIndex(null);
		const files = Array.from(e.dataTransfer.files).filter(isValidFontFile);
		if (files.length > 0) {
			const file = files[0];
			const format = getFormatFromFile(file);
			if (format) {
				onFileUpload(index, format, file);
			}
		}
	};

	const onBrowseClick = () => {
		fileInputRef.current?.click();
	};

	const onBrowseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		processDroppedFiles(files);
		e.target.value = '';
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderGoogleFontRow = (font: DesignSystemFont, index: number) => {
		const availableWeights = getAvailableWeights(font.fontFamily, googleFonts);
		const isCreating = creatingIndex === index;

		return (
			<S.FontCard key={index}>
				<S.FontTypeContainer>
					<Icon type={IconType.GOOGLE} width={20} height={20} />
				</S.FontTypeContainer>
				<S.FontNameContainer>
					<FontInput
						font={font.fontFamily}
						onFontChange={(family) => onFontFamilyChange(index, family)}
						onFontCommit={(family) => onFontFamilyCommit(index, family)}
						onCancel={() => onFontCancelCreate(index)}
						autoFocus={isCreating}
					/>
				</S.FontNameContainer>
				{!isCreating && font.fontFamily && (
					<S.WeightsContainer>
						{availableWeights.map((weight) => (
							<S.WeightPill key={weight} $checked={font.fontWeight?.includes(weight) || false}>
								<input
									type="checkbox"
									checked={font.fontWeight?.includes(weight) || false}
									onChange={() => onWeightToggle(index, weight)}
								/>
								{weight}
							</S.WeightPill>
						))}
					</S.WeightsContainer>
				)}
				<S.RemoveButton
					onClick={() => onRemoveFont(index)}
					disabled={value.length <= 1}
					title={value.length <= 1 ? 'At least one font required' : 'Remove font'}
				>
					<Icon type={IconType.TRASH} width={14} height={14} />
				</S.RemoveButton>
			</S.FontCard>
		);
	};

	const renderCustomFontRow = (font: DesignSystemFont, index: number) => {
		const isUploadingAny = !!uploadingFiles[`${index}-woff2`] || !!uploadingFiles[`${index}-woff`];
		const hasFile = font.files?.woff2 || font.files?.woff;
		const fileFormat = font.files?.woff2 ? 'WOFF2' : 'WOFF';

		return (
			<S.FontCard
				key={index}
				$isDragOver={dragOverIndex === index}
				onDragEnter={(e) => onRowDragEnter(e, index)}
				onDragLeave={onRowDragLeave}
				onDragOver={(e) => e.preventDefault()}
				onDrop={(e) => onRowDrop(e, index)}
			>
				<S.FontTypeContainer>
					<Icon type={IconType.CUSTOM_FONT} width={20} height={20} />
				</S.FontTypeContainer>
				<S.FontNameContainer>
					{isUploadingAny && !font.fontFamily ? (
						<S.UploadingLabel>Uploading…</S.UploadingLabel>
					) : (
						<FontInput
							font={font.fontFamily}
							onFontChange={(family) => {
								const newFonts = [...value];
								newFonts[index] = { ...newFonts[index], fontFamily: family };
								onChange(newFonts);
							}}
							onFontCommit={(family) => {
								if (value[index]?.fontFamily === family) return;
								const newFonts = [...value];
								newFonts[index] = { ...newFonts[index], fontFamily: family };
								if (onFontCommit) onFontCommit(index, newFonts);
							}}
						/>
					)}
				</S.FontNameContainer>
				{hasFile ? (
					<S.FileTile onClick={() => triggerFileUpload(index)}>
						<S.FileTileLabel>{fileFormat}</S.FileTileLabel>
						<S.FileTileHoverLabel>Replace</S.FileTileHoverLabel>
					</S.FileTile>
				) : (
					<S.UploadButton onClick={() => triggerFileUpload(index)} disabled={isUploadingAny}>
						<Icon type={IconType.UPLOAD} width={12} height={12} />
						{isUploadingAny ? 'Uploading...' : 'Upload font'}
					</S.UploadButton>
				)}
				<S.RemoveButton
					onClick={() => onRemoveFont(index)}
					disabled={value.length <= 1}
					title={value.length <= 1 ? 'At least one font required' : 'Remove font'}
				>
					<Icon type={IconType.TRASH} width={14} height={14} />
				</S.RemoveButton>
			</S.FontCard>
		);
	};

	return (
		<S.Container>
			<S.FontList>
				{value.map((font, index) =>
					font.source === 'custom'
						? renderCustomFontRow(font, index)
						: renderGoogleFontRow(font, index)
				)}
			</S.FontList>
			<S.AddGoogleFontLink onClick={onAddGoogleFont}>
				<Icon type={IconType.GOOGLE} width={16} height={16} />
				Add Google Font
				{googleFonts.length > 0 && (
					<S.AddGoogleFontSubtitle>
						— {googleFonts.length.toLocaleString()} available
					</S.AddGoogleFontSubtitle>
				)}
			</S.AddGoogleFontLink>
			<S.DropZone
				$isDragging={isDragging}
				onDragEnter={onDragEnter}
				onDragLeave={onDragLeave}
				onDragOver={onDragOver}
				onDrop={onDrop}
				onClick={onBrowseClick}
			>
				<Icon type={IconType.UPLOAD} width={22} height={22} />
				<S.DropZoneLabel>Drop font files or click to browse</S.DropZoneLabel>
				<S.DropZoneFormats>.woff2 or .woff</S.DropZoneFormats>
			</S.DropZone>
			<input
				ref={fileInputRef}
				type="file"
				accept=".woff,.woff2"
				multiple
				style={{ display: 'none' }}
				onChange={onBrowseChange}
			/>
		</S.Container>
	);
};

export default FontSelector;
